import { TREND_STATUS } from 'shared/constants/quality-metric.constants';
import { displayPercentage, isSuccess } from 'shared/utils/quality-metrics.utils';
import { MetricPropBox } from 'src/components/quality-metric/quality-metric-prop-box/quality-metric-prop-box';

/**
 * Shared metric display — title + score/trend badge.
 * Used by the dashboard widget (as a Link) and the sidebar (as a button).
 *
 * @param {object}  props
 * @param {object}  props.metric      – metric record (title, totalRecords, totalSuccess, groupGoal, invert, minimumRecords)
 * @param {object}  [props.trend]     – trend delta (from deriveMetricsTrends)
 * @param {boolean} [props.showIcon]  – forward to MetricPropBox (default true)
 * @param {string}  [props.titleClassName] – optional class for the title span
 * @param {React.ElementType} [props.TitleWrapper] – wrapper around the title (e.g. Tooltip)
 */
export function QualityMetricBox({ metric, trend, showIcon, titleClassName, TitleWrapper }) {
  const { title, totalRecords, totalSuccess, groupGoal, invert, minimumRecords } = metric;

  const metricSuccess = isSuccess({
    total: totalRecords,
    success: totalSuccess,
    groupGoal,
    invert,
  });

  const percentage = displayPercentage({
    total: totalRecords,
    success: totalSuccess,
    invert,
  });

  const titleEl = <span className={titleClassName}>{title}</span>;

  return (
    <>
      {TitleWrapper ? <TitleWrapper>{titleEl}</TitleWrapper> : titleEl}
      <MetricPropBox
        percentage={percentage}
        minimumRecords={minimumRecords}
        totalRecords={totalRecords}
        isSuccess={metricSuccess}
        trend={trend}
        showIcon={showIcon}
      />
    </>
  );
}

/**
 * Build an aria-label string for a metric box.
 * Extracted so both widget (Link) and sidebar (button) can reuse it.
 */
export function getMetricAriaLabel({ metric, trend, lang }) {
  const { title, totalRecords, totalSuccess, groupGoal, invert, minimumRecords } = metric;

  const hasInsufficientData = minimumRecords && totalRecords < minimumRecords;
  if (hasInsufficientData) {
    return `${title}, ${lang.NO_DATA}`;
  }

  const metricSuccess = isSuccess({
    total: totalRecords,
    success: totalSuccess,
    groupGoal,
    invert,
  });
  const percentage = displayPercentage({ total: totalRecords, success: totalSuccess, invert });
  const status = metricSuccess ? lang.PASSING : lang.NOT_PASSING;
  const invertedText = invert ? `, ${lang.INVERTED_METRIC}` : '';

  let trendText = '';
  if (trend && trend.status !== TREND_STATUS.NA) {
    if (trend.status === TREND_STATUS.NO_CHANGE) {
      trendText = `, ${lang.TREND_NO_CHANGE}`;
    } else {
      const sign = trend.delta > 0 ? '+' : '';
      trendText = `, ${sign}${trend.delta}%`;
    }
  }

  return `${title}, ${percentage}, ${status}${invertedText}${trendText}`;
}
